import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Divider,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  VolumeUp as VolumeUpIcon,
  Comment as CommentIcon,
} from "@mui/icons-material";
import { useGetQuestionsByPartIdQuery } from "@/store/api/adminApi";
import { openImagePreview, openRichTextPreview } from "@/store/slices/contentPreviewSlice";

const PartQuestionList = ({
  examId,
  part,
  onEditQuestion,
  onDeleteQuestion,
  setQuestions,
}) => {
  const dispatch = useDispatch();
  const [playingAudio, setPlayingAudio] = useState(null);
  const { data: partQuestions } = useGetQuestionsByPartIdQuery({
    examId,
    partId: part._id,
  });

  useEffect(() => {
    setQuestions?.(partQuestions?.data || []);
  }, [partQuestions]);

  const questions = useMemo(() => partQuestions?.data || [], [partQuestions]);

  const handleImageClick = (imageUrl) => {
    dispatch(openImagePreview(imageUrl));
  };

  const handleContentClick = (content) => {
    dispatch(openRichTextPreview(content));
  };

  const handleAudioPlay = (audioUrl) => {
    setPlayingAudio(audioUrl === playingAudio ? null : audioUrl);
  };

  return (
    <Box sx={{ position: "relative" }}>
      {questions.length === 0 ? (
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          No questions have been added to this part yet.
        </Alert>
      ) : (
        <List disablePadding>
          {questions.map((question, index) => (
            <React.Fragment key={question._id}>
              <ListItem
                disablePadding
                sx={{
                  "&:hover": { backgroundColor: "action.hover" },
                  transition: "background-color 0.2s",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    p: 1,
                    pl: 0,
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 40, pt: 0.5 }}
                    >
                      {question.number}.
                    </Typography>
                    <Box sx={{ flexGrow: 1, mr: 2 }}>
                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        {question.question}
                      </Typography>
                      <Chip
                        label={
                          question.section.charAt(0).toUpperCase() +
                          question.section.slice(1)
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          height: "24px",
                          fontSize: "0.75rem",
                          mt: 0.5,
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    {question.audioUrl && (
                      <IconButton
                        onClick={() => handleAudioPlay(question.audioUrl)}
                        size="small"
                        color={
                          playingAudio === question.audioUrl
                            ? "primary"
                            : "default"
                        }
                      >
                        <VolumeUpIcon />
                      </IconButton>
                    )}
                    {playingAudio === question.audioUrl && (
                      <audio
                        src={question.audioUrl}
                        autoPlay
                        onEnded={() => setPlayingAudio(null)}
                        style={{ display: "none" }}
                      />
                    )}
                    {question.imageUrl && (
                      <Avatar
                        variant="rounded"
                        src={question.imageUrl}
                        sx={{ width: 56, height: 56, cursor: "pointer" }}
                        onClick={() => handleImageClick(question.imageUrl)}
                      >
                        <ImageIcon />
                      </Avatar>
                    )}
                    {question.extraImageUrl && (
                      <Avatar
                        variant="rounded"
                        src={question.extraImageUrl}
                        sx={{ width: 56, height: 56, cursor: "pointer" }}
                        onClick={() => handleImageClick(question.extraImageUrl)}
                      >
                        <ImageIcon />
                      </Avatar>
                    )}
                  </Box>

                  <Box sx={{ flexShrink: 0, ml: 1 }}>
                    {question.content && (
                      <IconButton
                        aria-label="content"
                        onClick={() => handleContentClick(question.content)}
                      >
                        <CommentIcon />
                      </IconButton>
                    )}
                    <IconButton
                      aria-label="edit"
                      onClick={() => onEditQuestion(question, part?._id)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      onClick={() => onDeleteQuestion(question)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </ListItem>
              {index < questions.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
};

export default PartQuestionList;
